import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Grid,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import Title from "../Title";
import MultiValueInput from "../../inputs/multiValueInput";
import MultiSelector from "../../inputs/MultiSelector";
import SearchDateInput from "../../inputs/searchDateInput";
import {
  clearParameters,
  removeParameter,
  setDateParameters,
  setParameters,
} from "../../redux/global/actions";
import {
  getHwsSnTrailAnalysis,
  getHwsSnTrailsBulk,
} from "../../redux/dynamo_db/actions";
import {
  downloadCSV,
  extractUserInfoFromToken,
  getLocalStorageSAMLToken,
} from "../../utils";
import { hwsTrailAnalysisColumns } from "./hwsTrailAnalysisColumns";

const FAMILY = "hwsSnsTrailAnalysis";
const SEARCH_LIMIT = 100;

const statusValues = [
  "To do",
  "Done",
  "Error",
  "Automatic Swap Deactivated",
  "Manual Check Required",
];
const productTypeValues = ["DATA", "VOICE", "UNDEFINED"];

// Columns displayed in the results table (the CSV export carries every column).
const displayedColumns = [
  { name: "hws_identification_date", label: "Creation Date" },
  { name: "hws_order_id", label: "Order ID" },
  { name: "hws_base_order_line", label: "Order Line" },
  { name: "product_type", label: "Product Type" },
  { name: "status", label: "Status" },
  { name: "serial_number_hws_in", label: "SN HWS IN" },
  { name: "part_number_hws_in", label: "PN HWS IN" },
  { name: "hws_in_arrival_date", label: "Arrival Date" },
  { name: "business_partner_hws_in", label: "BP HWS IN" },
  { name: "serial_number_hws_out", label: "SN HWS OUT" },
  { name: "part_number_hws_out", label: "PN HWS OUT" },
  { name: "hws_out_departure_date", label: "Departure Date" },
  { name: "business_partner_hws_out", label: "BP HWS OUT" },
  { name: "swap_date", label: "Swap Date" },
  { name: "replaced_by", label: "Replaced By" },
  { name: "error_reason", label: "Error Reason" },
];

const modalButtonStyle = { color: "white", background: "#6b469e" };

function MessageModal({ open, message, onClose }) {
  return (
    <Modal open={open} style={{ textAlign: "center", paddingTop: "45vh" }}>
      <>
        <Typography style={{ color: "white" }}>{message}</Typography>
        <div style={{ paddingBottom: "1%" }} />
        <Grid container className="headercontainer">
          <Grid item>
            <Button style={modalButtonStyle} onClick={onClose}>
              Ok
            </Button>
          </Grid>
        </Grid>
      </>
    </Modal>
  );
}

export default function HwsTrailAnalysis() {
  const dispatch = useDispatch();
  const queryParameters = useSelector((state) => state?.parameters?.[FAMILY]);
  const lines = useSelector((state) =>
    Array.isArray(state?.hwsSnsTrailAnalysis) ? state.hwsSnsTrailAnalysis : [],
  );

  const [currentOrderId, setCurrentOrderId] = React.useState();
  const [currentOrderLine, setCurrentOrderLine] = React.useState();
  const [openDateModal, setOpenDateModal] = React.useState(false);
  const [openBulkConfirmation, setOpenBulkConfirmation] = React.useState(false);
  const [openBulkMessage, setOpenBulkMessage] = React.useState(false);

  // An order search when order ids are given, otherwise a date search (the date range is then mandatory).
  const buildQuery = () => {
    const query = {};
    if (queryParameters?.orderIds?.length) {
      query.search_type = "order";
      query.order_id = queryParameters.orderIds;
      if (queryParameters?.orderLines?.length)
        query.order_line = queryParameters.orderLines;
    } else if (
      queryParameters?.creationDateStart &&
      queryParameters?.creationDateEnd
    ) {
      query.search_type = "date";
      query.creation_date_from = queryParameters.creationDateStart;
      query.creation_date_to = queryParameters.creationDateEnd;
    } else {
      return null;
    }
    if (queryParameters?.status?.length) query.status = queryParameters.status;
    if (queryParameters?.productType?.length)
      query.product_type = queryParameters.productType;
    return query;
  };

  const handleExecuteSearch = () => {
    const query = buildQuery();
    if (!query) {
      setOpenDateModal(true);
      return;
    }
    dispatch(getHwsSnTrailAnalysis(query));
  };

  const handleExecuteBulkSearch = () => {
    const query = buildQuery();
    if (!query) {
      setOpenDateModal(true);
      return;
    }
    const infoToken = extractUserInfoFromToken(getLocalStorageSAMLToken());
    dispatch(
      getHwsSnTrailsBulk({
        ...query,
        only_hws_in: false,
        combine: "and",
        email: infoToken.email,
        request_user: infoToken.username,
      }),
    );
    setOpenBulkMessage(true);
  };

  const handleExport = () => {
    const fields = hwsTrailAnalysisColumns.map((column) => ({
      name: column.name,
    }));
    const rows = lines.map((line) => ({
      ...line,
      hws_in_purchased_contracts: line.hws_in_purchased_contracts
        ? JSON.stringify(line.hws_in_purchased_contracts)
        : undefined,
    }));
    downloadCSV(fields, "hardware_support_process_analysis", ",", rows);
  };

  const chipHandlers = (type) => ({
    handleDelete: (chipToDelete) => () =>
      dispatch(removeParameter({ [FAMILY]: { [type]: [chipToDelete] } })),
    handleClear: () => dispatch(clearParameters({ [FAMILY]: type })),
  });

  return (
    <React.Fragment>
      <MessageModal
        open={openDateModal}
        message="The Search Date values are mandatory when no Order ID is given."
        onClose={() => setOpenDateModal(false)}
      />
      <MessageModal
        open={openBulkMessage}
        message="Your query has been sent. You can follow the status in the Bulk Report page or wait for the Rainbow message that will announce the end of the export."
        onClose={() => setOpenBulkMessage(false)}
      />
      <Modal
        open={openBulkConfirmation}
        style={{ textAlign: "center", paddingTop: "45vh" }}
      >
        <>
          <Typography style={{ color: "white" }}>
            This query could take a few minutes, are you sure you want to
            continue?
          </Typography>
          <div style={{ paddingBottom: "1%" }} />
          <Grid container className="headercontainer">
            <Grid item>
              <Button
                style={modalButtonStyle}
                onClick={() => {
                  setOpenBulkConfirmation(false);
                  handleExecuteBulkSearch();
                }}
              >
                Yes
              </Button>
            </Grid>
            <Grid item style={{ width: "7%" }} />
            <Grid item>
              <Button
                style={modalButtonStyle}
                onClick={() => setOpenBulkConfirmation(false)}
              >
                No
              </Button>
            </Grid>
          </Grid>
        </>
      </Modal>

      <Typography variant="body2" sx={{ mb: 2 }}>
        Every line of the Hardware Support trail, including the lines that only
        have a replacement (HWS OUT) or only a returned asset (HWS IN). Search
        by creation date, or by order when Order IDs are given; the other
        filters are combined (AND).
      </Typography>

      <MultiValueInput
        setCurrentParam={setCurrentOrderId}
        setQueryParams={setParameters}
        currentParam={currentOrderId}
        queryParams={queryParameters?.orderIds}
        {...chipHandlers("orderIds")}
        label="Order ID"
        family={FAMILY}
        type="orderIds"
      />
      <MultiValueInput
        setCurrentParam={setCurrentOrderLine}
        setQueryParams={setParameters}
        currentParam={currentOrderLine}
        queryParams={queryParameters?.orderLines}
        {...chipHandlers("orderLines")}
        label="Order Line (with an Order ID)"
        family={FAMILY}
        type="orderLines"
      />
      <MultiSelector
        setQueryParams={setParameters}
        queryParams={queryParameters?.status}
        {...chipHandlers("status")}
        label="Status"
        family={FAMILY}
        type="status"
        options={statusValues}
      />
      <MultiSelector
        setQueryParams={setParameters}
        queryParams={queryParameters?.productType}
        {...chipHandlers("productType")}
        label="Product Type"
        family={FAMILY}
        type="productType"
        options={productTypeValues}
      />
      <Grid>
        <SearchDateInput
          title="Creation Date (mandatory without Order ID)"
          setFilterDate={setDateParameters}
          family={FAMILY}
          type="creationDateStart"
          type2="creationDateEnd"
        />
      </Grid>

      <Grid container style={{ justifyContent: "center", marginTop: "20px" }}>
        <Grid item>
          <Button
            variant="contained"
            onClick={handleExecuteSearch}
            style={{ width: "15vw" }}
          >
            Search
          </Button>
        </Grid>
        <div style={{ paddingLeft: "0.2%" }} />
        <Grid item>
          <Button
            variant="contained"
            onClick={() => setOpenBulkConfirmation(true)}
            style={{ width: "15vw" }}
          >
            Bulk Export
          </Button>
        </Grid>
        <div style={{ paddingLeft: "0.2%" }} />
        <Grid item>
          <Button
            variant="outlined"
            onClick={handleExport}
            disabled={!lines.length}
            style={{ width: "15vw" }}
          >
            Export results (CSV)
          </Button>
        </Grid>
      </Grid>

      <Accordion sx={{ mt: 3 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>Available columns (CSV export)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell className="table-header-cell">Field Name</TableCell>
                <TableCell className="table-header-cell">Description</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {hwsTrailAnalysisColumns.map((column) => (
                <TableRow key={column.name} hover>
                  <TableCell>
                    <code>{column.name}</code>
                  </TableCell>
                  <TableCell>{column.description}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </AccordionDetails>
      </Accordion>

      <Box sx={{ mt: 3 }}>
        <Title>Hardware Support Process - Analysis</Title>
        <Typography>
          {lines.length
            ? lines.length === SEARCH_LIMIT
              ? lines.length + " results, use bulk export for more"
              : lines.length + " results"
            : null}
        </Typography>
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {displayedColumns.map((column) => (
                  <TableCell key={column.name}>{column.label}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {lines.map((line) => (
                <TableRow key={line.SK} hover>
                  {displayedColumns.map((column) => (
                    <TableCell key={column.name}>{line[column.name]}</TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Box>
    </React.Fragment>
  );
}
